import { useEffect, useState } from "react";
import { Megaphone, Copy, Sparkles, Clock, Target, TrendingUp, Tag, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import StatCard from "../components/StatCard";

function typeLabel(t) {
  return ({
    deposit_bonus: "Deposit Bonus",
    free_spin: "Free Spin",
    cashback: "Cashback",
    first_deposit: "First Deposit",
    vip_invite: "VIP Invite",
  })[t] || t;
}

function badgeFor(status) {
  if (status === "active") return { c: "#6ee7b7", b: "rgba(16,185,129,0.35)", g: "rgba(16,185,129,0.12)" };
  if (status === "paused") return { c: "#fca5a5", b: "rgba(244,180,49,0.35)", g: "rgba(244,180,49,0.12)" };
  return { c: "#cbd5e1", b: "rgba(148,163,184,0.32)", g: "rgba(148,163,184,0.10)" };
}

export default function PromoCampaigns() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/aff-panel/campaigns");
      setRows(r.data?.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load campaigns");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const copy = (t) => {
    navigator.clipboard.writeText(t).then(() => toast.success("Copied"));
  };

  const totals = rows.reduce((a, c) => {
    a.clicks += c.stats?.clicks || 0;
    a.conv += c.stats?.conversions || 0;
    a.deposits += c.stats?.deposit_total || 0;
    return a;
  }, { clicks: 0, conv: 0, deposits: 0 });

  return (
    <div>
      <PageHeader
        eyebrow="MARKETING"
        title="Active Campaigns"
        subtitle="Promote special bonuses with your own tracking link — get extra commission on every signup."
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <StatCard label="Active Campaigns" value={rows.length} icon={Megaphone} accent="gold" />
        <StatCard label="Total Clicks" value={totals.clicks} icon={TrendingUp} accent="emerald" />
        <StatCard label="Conversions" value={totals.conv} icon={Target} accent="amber" />
        <StatCard label="Tracked Deposits" value={`৳${totals.deposits.toLocaleString()}`} icon={Sparkles} accent="blue" />
      </div>

      {loading ? (
        <div className="p-glass p-6 text-center text-white/55">Loading campaigns…</div>
      ) : rows.length === 0 ? (
        <div className="p-glass p-10 text-center">
          <div className="mx-auto mb-3 w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: "rgba(34, 192, 115, 0.10)", border: "1px solid rgba(34, 192, 115, 0.3)" }}>
            <Megaphone size={26} className="text-emerald-300" />
          </div>
          <div className="text-white font-bold text-lg mb-1">No active campaigns yet</div>
          <div className="text-white/55 text-sm">When admin launches a campaign you'll see it here with your own tracking link.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {rows.map((c) => {
            const b = badgeFor(c.status);
            return (
              <div key={c.id} className="p-glass p-card-hover" style={{ padding: 18 }}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="p-gold-chip" style={{ fontSize: 9 }}>{typeLabel(c.type)}</span>
                      <span className="text-[10px] uppercase tracking-[0.22em] font-bold px-2 py-0.5 rounded-md" style={{ color: b.c, background: b.g, border: `1px solid ${b.b}` }}>{c.status}</span>
                    </div>
                    <div className="font-black text-white text-lg leading-tight truncate">{c.name}</div>
                    <div className="text-white/55 text-[12.5px] mt-1">{c.description || "\u00a0"}</div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 my-3">
                  {c.bonus_pct > 0 && <Pill icon={Tag} label={`Bonus ${c.bonus_pct}%`} />}
                  {c.bonus_amount > 0 && <Pill icon={Tag} label={`৳${c.bonus_amount} bonus`} />}
                  {c.min_deposit > 0 && <Pill icon={Tag} label={`Min ৳${c.min_deposit}`} />}
                  {c.expires_at && <Pill icon={Clock} label={`Ends ${(c.expires_at || "").slice(0, 10)}`} />}
                </div>

                <div className="grid grid-cols-4 gap-2 text-center mb-3 p-3 rounded-xl" style={{ background: "rgba(0,0,0,0.30)", border: "1px solid rgba(255,255,255,0.06)" }}>
                  <Stat label="Clicks" value={c.stats?.clicks || 0} />
                  <Stat label="Conv." value={c.stats?.conversions || 0} />
                  <Stat label="CTR" value={`${(c.stats?.ctr || 0).toFixed(1)}%`} />
                  <Stat label="৳ Tracked" value={`৳${(c.stats?.deposit_total || 0).toLocaleString()}`} />
                </div>

                <div className="flex items-center gap-2">
                  <div className="flex-1 px-3 py-2 rounded-lg font-mono text-[12px] text-emerald-100 truncate" style={{ background: "rgba(34, 192, 115, 0.06)", border: "1px solid rgba(34, 192, 115, 0.20)" }}>
                    {c.tracking_link || `code: ${c.code}`}
                  </div>
                  <button onClick={() => copy(c.tracking_link || c.code)} className="p-btn-gold px-3 h-9 rounded-lg text-[12px] font-bold inline-flex items-center gap-1.5" data-testid={`copy-link-${c.id}`}>
                    <Copy size={13} /> Copy
                  </button>
                  {c.tracking_link && (
                    <a href={c.tracking_link} target="_blank" rel="noreferrer" className="p-btn-ghost px-3 h-9 rounded-lg text-[12px] font-bold inline-flex items-center gap-1.5">
                      <ExternalLink size={13} />
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Pill({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] text-white/80 font-semibold" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
      {Icon && <Icon size={11} className="text-emerald-200" />} {label}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 font-bold">{label}</div>
      <div className="text-white font-black text-[14px] mt-0.5">{value}</div>
    </div>
  );
}
